import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, logout, oauthStartUrl } from '../api';
import { OAUTH_SCHEME_RETURN, isNative, openAuthUrl, registerPush } from '../native';
import type { Settings } from '../types';

const LABEL: Record<string, string> = { google: 'Google', facebook: 'Facebook' };

export function SettingsPage() {
  const [s, setS] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pw, setPw] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  const load = () => api.settings().then(setS).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    const frag = new URLSearchParams(loc.hash.replace(/^#/, ''));
    if (frag.get('linked')) setNotice(`${LABEL[frag.get('linked')!] ?? frag.get('linked')} linked`);
    if (frag.get('error')) setError(frag.get('error'));
  }, [loc.hash]);

  if (error && !s) return <div className="error">{error}</div>;
  if (!s) return <div className="spinner">Loading…</div>;

  const togglePush = async (on: boolean) => {
    setS({ ...s, pushEnabled: on });
    await api.updateSettings({ pushEnabled: on }).catch((e) => setError(e.message));
    if (on) {
      const r = await registerPush().catch(() => 'unavailable' as const);
      if (r === 'denied') setError('Notifications are blocked for Bandwagn in your device settings.');
      else if (r === 'unavailable' && 'Notification' in window && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
      if (r === 'registered') load();
    }
  };
  const unlink = async (p: string) => {
    if (!confirm(`Unlink ${LABEL[p] ?? p}?`)) return;
    try { await api.unlinkIdentity(p); load(); } catch (e: any) { setError(e.message); }
  };
  const savePw = async () => {
    try { await api.updateSettings({ password: pw }); setPw(''); setNotice('Password saved'); load(); } catch (e: any) { setError(e.message); }
  };

  const linked = new Set(s.identities.map((i) => i.provider));

  const deleteNow = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleteBusy(true);
    try {
      await api.deleteAccount();
      logout();
      nav('/login', { replace: true });
    } catch (e: any) { setError(e.message); setDeleteBusy(false); }
  };

  return (
    <div className="stack">
      {notice && <div className="ok">{notice}</div>}
      {error && <div className="error">{error}</div>}

      <div className="card">
        <h2>Notifications</h2>
        <label className="toggle">
          <input type="checkbox" checked={s.pushEnabled} onChange={(e) => togglePush(e.target.checked)} />
          <span>Push notifications <span className="muted small">(when someone wants to jam or messages you)</span></span>
        </label>
        <div className="muted small" style={{ marginTop: 6 }}>
          {s.pushDevices > 0 ? `${s.pushDevices} device${s.pushDevices === 1 ? '' : 's'} registered` : 'No device registered yet — install the app on your phone to receive pushes.'}
        </div>
      </div>

      <div className="card">
        <h2>Linked accounts</h2>
        {['google', 'facebook'].map((p) => (
          <div className="row between" key={p} style={{ padding: '8px 0' }}>
            <div>
              <strong>{LABEL[p]}</strong>
              <div className="muted small">{linked.has(p) ? (s.identities.find((i) => i.provider === p)?.email ?? 'Linked') : s.providers.includes(p) ? 'Not linked' : 'Not available yet'}</div>
            </div>
            {linked.has(p)
              ? <button className="btn ghost sm" onClick={() => unlink(p)}>Unlink</button>
              : s.providers.includes(p) ? <button className="btn secondary sm" onClick={() => openAuthUrl(oauthStartUrl(p, 'link', isNative() ? OAUTH_SCHEME_RETURN : undefined))}>Link</button> : null}
          </div>
        ))}
      </div>

      <div className="card">
        <h2>Account</h2>
        <div className="muted small" style={{ marginBottom: 8 }}>{s.email ?? 'No email on this account'}</div>
        <label className="field"><span>{s.hasPassword ? 'Change password' : 'Set a password'}</span>
          <div className="row">
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={8} autoComplete="new-password" placeholder="At least 8 characters" />
            <button className="btn sm" disabled={pw.length < 8} onClick={savePw}>Save</button>
          </div>
        </label>
        <button className="btn danger block" onClick={() => { logout(); nav('/login', { replace: true }); }}>Log out</button>
      </div>

      <div className="card">
        <h2>Delete account</h2>
        {!deleting ? (
          <>
            <div className="muted small" style={{ marginBottom: 10 }}>Permanently removes your profile, photos, messages, saved searches and activity. This cannot be undone.</div>
            <button className="btn danger block" onClick={() => setDeleting(true)}>Delete my account…</button>
          </>
        ) : (
          <div className="stack">
            <div className="error">This is permanent. Everything on your profile and all your conversations will be erased.</div>
            <label className="field"><span>Type DELETE to confirm</span>
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoCapitalize="characters" autoComplete="off" placeholder="DELETE" />
            </label>
            <div className="row">
              <button className="btn secondary" style={{ flex: 1 }} disabled={deleteBusy} onClick={() => { setDeleting(false); setConfirmText(''); }}>Keep my account</button>
              <button className="btn" style={{ flex: 1, background: 'var(--danger)' }} disabled={confirmText !== 'DELETE' || deleteBusy} onClick={deleteNow}>{deleteBusy ? 'Deleting…' : 'Delete forever'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
